/**
 * MCP server form component with Vuexy styling.
 * Provides form for creating and editing MCP servers with organized sections.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// MUI Imports
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

// Component Imports
import { TextField } from '@core/components/mui';
import { mcpService } from '../../services/mcpService';
import { useNotificationStore } from '../../store/notificationStore';

/**
 * MCP server form component for creating and editing MCP servers.
 */
export const McpServerForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [authenticationJson, setAuthenticationJson] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [healthCheckEndpoint, setHealthCheckEndpoint] = useState('');
  const [healthCheckIntervalMinutes, setHealthCheckIntervalMinutes] = useState<number | undefined>(5);
  const [metadataJson, setMetadataJson] = useState('');
  const [headers, setHeaders] = useState<Array<{ id: string; key: string; value: string }>>([]);

  useEffect(() => {
    if (isEdit && id) {
      loadServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadServer = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await mcpService.getMcpServer(id);
      setName(data.name);
      setDescription(data.description || '');
      setServerUrl(data.serverUrl);
      setAuthenticationJson(''); // Don't load auth JSON for security
      setIsActive(data.isActive);
      setHealthCheckEndpoint(data.healthCheckEndpoint || '');
      setHealthCheckIntervalMinutes(data.healthCheckIntervalMinutes);
      setMetadataJson('');
      if (data.headersJson && data.headersJson.trim()) {
        try {
          const parsed = JSON.parse(data.headersJson) as Record<string, string>;
          setHeaders(
            Object.entries(parsed).map(([k, v], i) => ({ id: `header-${i}-${k}`, key: k, value: v ?? '' }))
          );
        } catch {
          setHeaders([]);
        }
      } else {
        setHeaders([]);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load MCP server';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!serverUrl.trim()) {
      setError('Server URL is required');
      return;
    }

    // Validate URL format
    try {
      new URL(serverUrl.trim());
    } catch {
      setError('Server URL must be a valid URL');
      return;
    }

    // Validate health check endpoint if provided
    if (healthCheckEndpoint.trim()) {
      try {
        new URL(healthCheckEndpoint.trim());
      } catch {
        setError('Health check endpoint must be a valid URL');
        return;
      }
    }

    // Validate JSON fields if provided
    if (authenticationJson.trim()) {
      try {
        JSON.parse(authenticationJson.trim());
      } catch {
        setError('Authentication JSON must be valid JSON');
        return;
      }
    }

    if (metadataJson.trim()) {
      try {
        JSON.parse(metadataJson.trim());
      } catch {
        setError('Metadata JSON must be valid JSON');
        return;
      }
    }

    const headersObj: Record<string, string> = {};
    for (const h of headers) {
      const k = h.key?.trim();
      if (k) headersObj[k] = h.value?.trim() ?? '';
    }
    const headersJson = Object.keys(headersObj).length > 0 ? JSON.stringify(headersObj) : undefined;

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        serverUrl: serverUrl.trim(),
        authenticationJson: authenticationJson.trim() || undefined,
        headersJson,
        isActive,
        healthCheckEndpoint: healthCheckEndpoint.trim() || undefined,
        healthCheckIntervalMinutes: healthCheckIntervalMinutes || undefined,
        metadataJson: metadataJson.trim() || undefined,
      };

      if (isEdit && id) {
        await mcpService.updateMcpServer(id, data);
        addNotification('MCP server updated successfully', 'success');
      } else {
        await mcpService.createMcpServer(data);
        addNotification('MCP server created successfully', 'success');
      }
      navigate('/mcp-servers');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save MCP server';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate('/mcp-servers')} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEdit ? 'Edit MCP Server' : 'Create MCP Server'}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Server URL"
                fullWidth
                required
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                helperText="The MCP server endpoint URL (e.g., https://mcp.example.com)"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Authentication JSON"
                fullWidth
                multiline
                rows={4}
                value={authenticationJson}
                onChange={(e) => setAuthenticationJson(e.target.value)}
                helperText="JSON configuration for server authentication (optional)"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Custom Headers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Optional HTTP headers to send with every request to this MCP server (e.g. X-API-Key, Accept).
              </Typography>
              <Table size="small">
                <TableBody>
                  {headers.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ width: '40%', p: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Header name"
                          value={row.key}
                          onChange={(e) =>
                            setHeaders((prev) =>
                              prev.map((r) => (r.id === row.id ? { ...r, key: e.target.value } : r))
                            )
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ width: '50%', p: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Value"
                          value={row.value}
                          onChange={(e) =>
                            setHeaders((prev) =>
                              prev.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r))
                            )
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ width: 48, p: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => setHeaders((prev) => prev.filter((r) => r.id !== row.id))}
                          aria-label="Remove header"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                type="button"
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setHeaders((prev) => [
                    ...prev,
                    { id: `new-${Date.now()}-${Math.random()}`, key: '', value: '' },
                  ])
                }
                sx={{ mt: 1 }}
              >
                Add header
              </Button>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Health Check Endpoint"
                fullWidth
                value={healthCheckEndpoint}
                onChange={(e) => setHealthCheckEndpoint(e.target.value)}
                helperText="Optional custom health check endpoint URL"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Health Check Interval (minutes)"
                fullWidth
                type="number"
                value={healthCheckIntervalMinutes || ''}
                onChange={(e) =>
                  setHealthCheckIntervalMinutes(
                    e.target.value ? parseInt(e.target.value, 10) : undefined
                  )
                }
                helperText="Interval between health checks (default: 5 minutes)"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Metadata JSON"
                fullWidth
                multiline
                rows={3}
                value={metadataJson}
                onChange={(e) => setMetadataJson(e.target.value)}
                helperText="Optional metadata as JSON"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
            </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 6 }}>
                  <Button variant="outlined" onClick={() => navigate('/mcp-servers')}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
